import type { AdminUser, PermissionGroup, Role, RoleInput, UserInput, UserKind } from '@/api/accessApi'

/**
 * Users & roles forms (z8nrz7fxtz): pure state ↔ payload, validation and the
 * permission-matrix rules. The API is the authority; these keep the UI from
 * offering what it would refuse.
 */

// ─── Users ───────────────────────────────────────────────────────────────────

export interface UserForm {
  email: string
  firstName: string
  lastName: string
  kind: UserKind
  roleId: string
  title: string
  teamId: string
  tier: string
  customerId: string
  tenantId: string
  isSuperAdmin: boolean
}

export type UserFormErrors = Partial<Record<keyof UserForm, string>>

export function emptyUserForm(kind: UserKind = 'AGENT'): UserForm {
  return { email: '', firstName: '', lastName: '', kind, roleId: '', title: '', teamId: '', tier: '', customerId: '', tenantId: '', isSuperAdmin: false }
}

export function userToForm(u: AdminUser): UserForm {
  return {
    email: u.email,
    firstName: u.firstName,
    lastName: u.lastName,
    kind: u.kind,
    roleId: u.role?.id ?? '',
    title: u.title ?? '',
    teamId: u.team?.id ?? '',
    tier: u.tier === null ? '' : String(u.tier),
    customerId: u.customer?.id ?? '',
    tenantId: u.tenant?.id ?? '',
    isSuperAdmin: u.isSuperAdmin,
  }
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Error message keys (i18n), per field. `editing` = email and kind are fixed. */
export function validateUser(f: UserForm, editing: boolean): UserFormErrors {
  const e: UserFormErrors = {}
  if (!editing && !EMAIL.test(f.email.trim())) e.email = 'access.errors.email'
  if (!f.firstName.trim()) e.firstName = 'access.errors.required'
  if (!f.lastName.trim()) e.lastName = 'access.errors.required'
  if (!f.roleId) e.roleId = 'access.errors.role'
  if (f.kind === 'CUSTOMER' && !f.customerId) e.customerId = 'access.errors.customer'
  return e
}

/**
 * The create / update body. Kind-specific fields are only sent for that kind
 * (the API refuses a team on a customer user), blanks become null on update
 * so a cleared field is cleared, and superadmin is only sent when it changes.
 */
export function userPayload(f: UserForm, original?: AdminUser): UserInput {
  const blank = (v: string) => (v.trim() ? v.trim() : original ? null : undefined)
  const common: UserInput = {
    firstName: f.firstName.trim(),
    lastName: f.lastName.trim(),
    roleId: f.roleId,
    title: blank(f.title),
  }
  const kindPart: UserInput =
    f.kind === 'AGENT'
      ? { teamId: f.teamId || (original ? null : undefined), tier: f.tier ? Number(f.tier) : original ? null : undefined }
      : { tenantId: f.tenantId || (original ? null : undefined), ...(original ? {} : { customerId: f.customerId }) }
  const superPart: UserInput = (original ? f.isSuperAdmin !== original.isSuperAdmin : f.isSuperAdmin) ? { isSuperAdmin: f.isSuperAdmin } : {}
  const body: UserInput = { ...common, ...kindPart, ...superPart }
  if (!original) Object.assign(body, { email: f.email.trim().toLowerCase(), kind: f.kind })
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined)) as UserInput
}

// ─── Granting ────────────────────────────────────────────────────────────────

/** Mirrors the API: a superadmin grants anything, anyone else only what they hold. */
export function canGrant(me: { isSuperAdmin: boolean; permissions: readonly string[] }, keys: readonly string[]): boolean {
  if (me.isSuperAdmin) return true
  const held = new Set(me.permissions)
  return keys.every((k) => held.has(k))
}

/** The roles a user of `kind` can be given by `me`. */
export function assignableRoles(roles: readonly Role[], kind: UserKind, me: { isSuperAdmin: boolean; permissions: readonly string[] }): Role[] {
  return roles.filter((r) => r.kind === kind && canGrant(me, r.permissions))
}

// ─── Roles ───────────────────────────────────────────────────────────────────

export interface RoleForm {
  displayName: string
  slug: string
  kind: UserKind
  permissions: string[]
  customerWide: boolean
  /** One scope row, the common case: [] = every product / customer. */
  productIds: string[]
  customerIds: string[]
}

export type RoleFormErrors = Partial<Record<'displayName' | 'slug', string>>

export function emptyRoleForm(kind: UserKind = 'AGENT'): RoleForm {
  return { displayName: '', slug: '', kind, permissions: [], customerWide: false, productIds: [], customerIds: [] }
}

/** A role's scope rows union on the server; the form edits them as one row (the union). */
export function roleToForm(r: Role): RoleForm {
  const union = (pick: (s: Role['scopes'][number]) => string[]) =>
    r.scopes.some((s) => pick(s).length === 0) ? [] : [...new Set(r.scopes.flatMap(pick))]
  return {
    displayName: r.displayName,
    slug: r.slug,
    kind: r.kind,
    permissions: [...r.permissions],
    customerWide: r.customerWide,
    productIds: union((s) => s.productIds),
    customerIds: union((s) => s.customerIds),
  }
}

/** "QA Reviewer (EU)" → "qa_reviewer_eu"; always starts with a letter. */
export function slugFromName(name: string): string {
  const s = name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48)
  return /^[a-z]/.test(s) ? s : `role_${s}`.slice(0, 48)
}

const SLUG = /^[a-z][a-z0-9_]{1,47}$/

export function validateRole(f: RoleForm, editing: boolean): RoleFormErrors {
  const e: RoleFormErrors = {}
  if (!f.displayName.trim()) e.displayName = 'access.errors.required'
  if (!editing && !SLUG.test(f.slug)) e.slug = 'access.errors.slug'
  return e
}

export function rolePayload(f: RoleForm, editing: boolean): RoleInput {
  const base: RoleInput = { displayName: f.displayName.trim(), permissions: [...new Set(f.permissions)] }
  const kindPart: RoleInput =
    f.kind === 'CUSTOMER'
      ? { customerWide: f.customerWide }
      : { scopes: f.productIds.length || f.customerIds.length ? [{ productIds: f.productIds, customerIds: f.customerIds }] : [] }
  return editing ? { ...base, ...kindPart } : { ...base, ...kindPart, slug: f.slug, kind: f.kind }
}

/** The matrix for a role kind: customer roles only see customer-safe permissions. */
export function groupsForKind(groups: readonly PermissionGroup[], kind: UserKind): PermissionGroup[] {
  if (kind === 'AGENT') return [...groups]
  return groups
    .map((g) => ({ ...g, permissions: g.permissions.filter((p) => p.customerGrantable) }))
    .filter((g) => g.permissions.length > 0)
}

export function groupState(selected: readonly string[], group: PermissionGroup): 'all' | 'some' | 'none' {
  const on = group.permissions.filter((p) => selected.includes(p.key)).length
  return on === 0 ? 'none' : on === group.permissions.length ? 'all' : 'some'
}

/** Turns a whole group on or off, keeping every other selection. */
export function toggleGroup(selected: readonly string[], group: PermissionGroup, on: boolean): string[] {
  const keys = group.permissions.map((p) => p.key)
  return on ? [...new Set([...selected, ...keys])] : selected.filter((k) => !keys.includes(k))
}

export function togglePermission(selected: readonly string[], key: string): string[] {
  return selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]
}
